/**
 * Prospect-enrichment seam — LinkedIn + website evaluation.
 *
 * Same pattern as `argoWorker.ts`: when `VITE_ENRICH_API_URL` is set we
 * POST the prospect to a hosted enrichment worker and return its read.
 * When it's NOT set — local dev, demos — `isConfigured()` is false and
 * `enrichProspect` falls back to a *deterministic local simulation* that
 * derives a believable evaluation from the inputs (company, title, URLs)
 * so the feature is fully usable offline. No secrets in the client.
 *
 *   POST {base}/enrich  { name, company, title, linkedinUrl, website }
 *                       → { summary, signals[], fitScore? }
 */

import type { ProspectEnrichment } from '../db/types';

const BASE_URL: string | undefined = (
  import.meta.env.VITE_ENRICH_API_URL as string | undefined
)?.replace(/\/+$/, '');
const API_KEY = import.meta.env.VITE_ENRICH_API_KEY as string | undefined;

const TIMEOUT_MS = 30_000;

/** True when a real enrichment endpoint is configured. */
export function isConfigured(): boolean {
  return Boolean(BASE_URL);
}

/** Backend descriptor for status pills / debugging. */
export function enrichInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: BASE_URL ?? null };
}

export interface EnrichInput {
  name: string;
  company?: string;
  title?: string;
  linkedinUrl?: string;
  website?: string;
}

export class EnrichError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'EnrichError';
  }
}

/**
 * Evaluate a prospect. Returns a `ProspectEnrichment` ready to persist on
 * the person record. Never throws — a worker/network failure downgrades
 * to the local simulation so the button always produces a result.
 */
export async function enrichProspect(input: EnrichInput): Promise<ProspectEnrichment> {
  if (isConfigured()) {
    try {
      return await postEnrich(input);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo enrich] worker failed, using local simulation:', err);
    }
  }
  return simulateEnrichment(input);
}

async function postEnrich(input: EnrichInput): Promise<ProspectEnrichment> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE_URL}/enrich`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
      },
      body: JSON.stringify(input),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new EnrichError(`Enrich worker responded ${res.status} ${res.statusText}`);
    }
    const raw = (await res.json()) as {
      summary?: unknown;
      signals?: unknown;
      fitScore?: unknown;
    };
    const summary = typeof raw.summary === 'string' ? raw.summary : undefined;
    const signals = Array.isArray(raw.signals)
      ? raw.signals.filter((s): s is string => typeof s === 'string')
      : [];
    if (!summary && signals.length === 0) {
      throw new EnrichError('Enrich worker returned no content.');
    }
    return {
      status: 'done',
      summary,
      signals,
      fitScore: typeof raw.fitScore === 'number' ? raw.fitScore : scoreFromSignals(signals),
      evaluatedAt: new Date().toISOString(),
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Deterministic local simulation
// ---------------------------------------------------------------------------

/** Stable 0–1 hash so the same prospect always reads the same way. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

const GROWTH_SIGNALS = [
  'Hiring across GTM (2–3 open AE roles)',
  'Recent funding round signalling budget',
  'Leadership posting about scaling operations',
  'Active on LinkedIn — engages with industry content',
  'Website emphasises rapid product velocity',
];
const RISK_SIGNALS = [
  'Lean team — longer buying cycle likely',
  'No clear budget owner identified yet',
  'Quiet on social — lower intent signal',
  'Early-stage; may lack procurement process',
];

/** Map signals → a coarse 0–100 fit score. */
function scoreFromSignals(signals: string[]): number {
  const positives = signals.filter((s) => GROWTH_SIGNALS.includes(s)).length;
  const negatives = signals.filter((s) => RISK_SIGNALS.includes(s)).length;
  return Math.max(15, Math.min(95, 45 + positives * 14 - negatives * 9));
}

function simulateEnrichment(input: EnrichInput): ProspectEnrichment {
  const seed = `${input.name}|${input.company ?? ''}|${input.linkedinUrl ?? ''}|${input.website ?? ''}`;
  const r = hash01(seed);
  const hasLinkedIn = Boolean(input.linkedinUrl);
  const hasWebsite = Boolean(input.website);

  const signals: string[] = [];
  // Pick a deterministic blend of growth vs risk signals from the hash.
  const growthCount = 1 + Math.floor(r * 3); // 1–3
  for (let i = 0; i < growthCount; i++) {
    signals.push(GROWTH_SIGNALS[(Math.floor(r * 100) + i) % GROWTH_SIGNALS.length]);
  }
  if (r < 0.45) {
    signals.push(RISK_SIGNALS[Math.floor(r * 100) % RISK_SIGNALS.length]);
  }
  if (!hasLinkedIn) signals.push('No LinkedIn on file — add one for a deeper read');
  if (!hasWebsite) signals.push('No website on file — add one for a deeper read');

  const fitScore = scoreFromSignals(signals);
  const band = fitScore >= 70 ? 'strong' : fitScore >= 45 ? 'moderate' : 'speculative';
  const company = input.company ?? 'the company';
  const summary =
    `Automated read on ${input.name}${input.title ? ` (${input.title})` : ''} at ${company}. ` +
    `${band === 'strong' ? 'Strong' : band === 'moderate' ? 'Moderate' : 'Speculative'} fit ` +
    `(${fitScore}/100) based on ${hasLinkedIn ? 'LinkedIn activity' : 'available profile data'}` +
    `${hasWebsite ? ' and website signals' : ''}. ` +
    `${
      band === 'strong'
        ? 'Worth prioritising — move toward a discovery call.'
        : band === 'moderate'
          ? 'Nurture with targeted value before pushing for a meeting.'
          : 'Qualify further before investing rep time.'
    }`;

  return {
    status: 'done',
    summary,
    signals,
    fitScore,
    evaluatedAt: new Date().toISOString(),
  };
}
